import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { createRoot } from 'react-dom/client';
import { PenTool, Plus, Trash2, Download, Loader2, FileText, Upload, X, Eye, CheckCircle, Send, MessageSquare, Sparkles, User, Briefcase, GraduationCap, Wrench, Mail, Phone, MapPin, Globe, Mic, MicOff, AlertCircle, Info, Link2 } from 'lucide-react';
import BackButton from '../components/BackButton';
import { cvAutofill, createCv, getCvList, getCvDetail, deleteCv,
  getCvQuestions, applyFieldKey, cleanAnswer, isSkipAnswer, getCandidateProfile,
  saveCandidateAnswers, getCandidateAnswers } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { buildFlow, buildFlowFromApi, getActiveQuestions, getProgress, getCurrentSection, didSectionChange, isCvPhaseComplete } from '../data/cvFlowQuestions';
import { resolveImageUrl } from '../utils/resolveImageUrl';
import './CvBuilderPage.css';

const emptyExperience = { company: '', title: '', startDate: '', endDate: '', description: '' };
const emptyEducation = { school: '', degree: '', startDate: '', endDate: '' };

// Smart experience parser, handles multiple formats:
// "HireThink - CTO, 2025-Devam"
// "CTO · HireThink · Tam Zamanlı May 2014 – Nis 2015 · 1 Yıl İstanbul, Türkiye · Ofiste - Description"
// "Kariyer.net, Kıdemli Yazılım Uzmanı, 2015-2016"
function parseExpInput(text) {
  const result = { company: '', title: '', startDate: '', endDate: '', scope: '' };

  // Tarih aralığını bul ve çıkar
  const dateRangeMatch = text.match(
    /(?:(?:Oca|Şub|Mar|Nis|May|Haz|Tem|Ağu|Eyl|Eki|Kas|Ara|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-zçğıöşü]*\.?\s*)?(\d{4})\s*[-–—]\s*(?:(?:Oca|Şub|Mar|Nis|May|Haz|Tem|Ağu|Eyl|Eki|Kas|Ara|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-zçğıöşü]*\.?\s*)?(\d{4}|[Dd]evam|[Hh]alen|[Pp]resent|[Gg]ünümüz)/i
  );

  if (dateRangeMatch) {
    result.startDate = dateRangeMatch[1];
    result.endDate = /devam|halen|present|günümüz/i.test(dateRangeMatch[2]) ? 'Devam' : dateRangeMatch[2];
  } else {
    const singleDates = text.match(/\d{4}/g);
    if (singleDates) {
      result.startDate = singleDates[0];
      if (singleDates[1]) result.endDate = singleDates[1];
      if (/devam|halen|present|günümüz/i.test(text)) result.endDate = 'Devam';
    }
  }

  // Tarihleri, süreleri ve lokasyon bilgilerini metinden temizle
  let cleaned = text
    .replace(/(?:Oca|Şub|Mar|Nis|May|Haz|Tem|Ağu|Eyl|Eki|Kas|Ara|Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[a-zçğıöşü]*\.?\s*\d{4}/gi, '')
    .replace(/\d{4}\s*[-–—]\s*(\d{4}|[Dd]evam|[Hh]alen|[Pp]resent|[Gg]ünümüz)/gi, '')
    .replace(/\d{4}/g, '')
    .replace(/\d+\s*(yıl|ay|year|month)/gi, '')
    .replace(/·/g, ',')
    .replace(/\s*[-–—]\s*/g, ', ')
    .replace(/,\s*,/g, ',')
    .trim();

  // Tam Zamanlı, Part-time vb. kaldır
  cleaned = cleaned.replace(/\b(tam zamanlı|yarı zamanlı|full[\s-]?time|part[\s-]?time|sözleşmeli|freelance)\b/gi, '');
  // Lokasyon/ofis bilgisini kaldır
  cleaned = cleaned.replace(/\b(ofiste|uzaktan|remote|hibrit|hybrid)\b/gi, '');
  // Şehir, Türkiye pattern
  cleaned = cleaned.replace(/,?\s*[A-ZÇĞİÖŞÜa-zçğıöşü]+\s*,?\s*türkiye/gi, '');

  // Virgülle ayır ve temizle
  const parts = cleaned.split(',').map(s => s.trim()).filter(s => s.length > 1);

  if (parts.length >= 2) {
    // İlk iki parçayı firma ve pozisyon olarak al
    // Hangisi firma, hangisi pozisyon? Genelde kısa olan pozisyon
    const [a, b] = parts;
    if (a.length > b.length) {
      result.company = a;
      result.title = b;
    } else {
      result.title = a;
      result.company = b;
    }
    // Kalan parçaları açıklama olarak al
    if (parts.length > 2) {
      result.scope = parts.slice(2).join('. ').trim();
    }
  } else if (parts.length === 1) {
    result.company = parts[0];
  }

  return result;
}

const ENGLISH_LEVEL_LABELS = { basic: 'Temel (A1-A2)', intermediate: 'Orta (B1-B2)', advanced: 'İleri (C1-C2)', fluent: 'Akıcı' };

// BE ileride yeni alanlar eklerse (askerlik durumu, medeni hal, doğum tarihi,
// uyruk vb.) burada bilinen bir etiket yoksa camelCase → Title Case çevrilir,
// bkz. AdminCandidateDetailPage'deki aynı desen.
const EXTRA_FIELD_LABELS = {
  birthDate: 'Doğum Tarihi', maritalStatus: 'Medeni Hal', militaryStatus: 'Askerlik Durumu',
  nationality: 'Uyruk', gender: 'Cinsiyet', title: 'Pozisyon', linkedin: 'LinkedIn',
  website: 'Web Sitesi', birthPlace: 'Doğum Yeri', workEnvironment: 'Tercih Edilen Çalışma Ortamı',
  sportsHobbies: 'Hobiler', practicalSkills: 'Pratik Beceriler',
};

function toTitleCase(camel) {
  return String(camel).replace(/([a-z])([A-Z])/g, '$1 $2').replace(/^./, (s) => s.toLocaleUpperCase('tr-TR'));
}

function extraFieldLabel(key) {
  return EXTRA_FIELD_LABELS[key] || toTitleCase(key);
}

// BE, groupKey'siz (kök) bazı sorularda fieldKey'i boş bırakıyor (ör. birthDate,
// workEnvironment, sportsHobbies, practicalSkills, canlıda GetCvQuestions ile
// doğrulandı). Dolu olan TÜM kök fieldKey'ler "cv.<key>" örüntüsünü izlediği için
// (cv.otherLanguages ← key=otherLanguages) aynı örüntüyle senkron bir fallback
// üretiyoruz. Grup alanlarında (education.* vb.) anahtar→alan eşlemesi düzensiz
// olduğu için (eduSchool → education.schoolName) burada TAHMİN YAPILMAZ; BE
// fieldKey eklemeden bu cevaplar (eduLevel, eduFaculty vb.) kaydedilemez.
function resolveFieldKey(q) {
  return q.fieldKey || (!q.groupKey ? `cv.${q.field}` : null);
}

// GetCvDetail'in bilinen alanları (fullName, experiences vb.) dışında kalan,
// değeri boş/"yok" olmayan tüm alanları otomatik olarak "Ek Bilgiler" bölümünde
// gösterir, BE'ye yeni bir alan eklenirse kod değişmeden görünür.
const KNOWN_CV_DETAIL_KEYS = new Set([
  'fullName', 'FullName', 'title', 'Title', 'email', 'Email', 'phone', 'Phone',
  'location', 'Location', 'linkedin', 'Linkedin', 'LinkedIn',
  'summary', 'Summary', 'skills', 'Skills', 'certificates', 'Certificates',
  'driverLicense', 'DriverLicense', 'englishLevel', 'EnglishLevel',
  'otherLanguages', 'OtherLanguages', 'experiences', 'Experiences', 'educations', 'Educations',
  'id', 'Id', 'name', 'Name', 'source', 'Source', 'candidateId', 'CandidateId',
  'createdAt', 'CreatedAt', 'updatedAt', 'UpdatedAt',
]);

function extractExtraFields(raw) {
  const extra = {};
  Object.keys(raw || {}).forEach((k) => {
    if (KNOWN_CV_DETAIL_KEYS.has(k)) return;
    const v = raw[k];
    if (v === null || v === undefined || v === '') return;
    if (typeof v === 'object') return; // dizi/obje alanlar burada ele alınmıyor
    const strVal = String(v).trim();
    if (!strVal || strVal.toLowerCase() === 'yok') return;
    extra[k] = strVal;
  });
  return extra;
}

// Sohbette toplanan cevaplardan, önizlemede zaten ayrıca gösterilen alanlar
// dışında kalanları ("Ek Bilgiler" için) süzer.
const KNOWN_ANSWER_FIELDS = new Set([
  'fullName', 'title', 'email', 'phone', 'location', 'linkedin', 'summary', 'skills',
  'certificates', 'driverLicense', 'englishLevel', 'otherLanguages', 'cvName', 'abroadInterest',
]);

function extraAnswerEntries(answers) {
  return Object.entries(answers || {}).filter(([k, v]) => {
    if (k.startsWith('_') || k.startsWith('__')) return false;
    if (KNOWN_ANSWER_FIELDS.has(k)) return false;
    if (typeof v !== 'string') return false;
    const trimmed = v.trim();
    if (!trimmed || trimmed.toLowerCase() === 'yok') return false;
    return true;
  });
}

// Kayıtlı CV'de kişisel/ATS alanları (isim, iletişim, askerlik durumu, uyruk,
// cinsiyet vb.) boşsa, adayın Profilim sayfasında zaten doldurduğu bilgilerle
// tamamlar, CV özelinde tekrar sorulmasına gerek kalmaz. GetCandidateProfile
// alan adları: name, surname, email, phone, location, gender, nationality,
// militaryStatus (bkz. ProfilePage.js).
function mergeWithCandidateProfile(cv, profile) {
  if (!profile) return cv;
  const fullName = cv.fullName || [profile.name, profile.surname].filter(Boolean).join(' ');
  const extra = { ...cv.extra };
  if (!extra.militaryStatus && profile.militaryStatus) extra.militaryStatus = profile.militaryStatus;
  if (!extra.nationality && profile.nationality) extra.nationality = profile.nationality;
  if (!extra.gender && profile.gender) extra.gender = profile.gender;
  return {
    ...cv,
    fullName,
    email: cv.email || profile.email || '',
    phone: cv.phone || profile.phone || '',
    location: cv.location || profile.location || '',
    photoUrl: cv.photoUrl || resolveImageUrl(profile.imageUrl) || '',
    extra,
  };
}

// PDF indirme: eskiden window.open() + tarayıcının "Yazdır" diyaloğuna
// dayanıyordu, Safari (özellikle masaüstü) popup'ı senkron açılsa bile
// zaman zaman sessizce engelliyor, kullanıcıya "pop-up engelleyicinizi
// kontrol edin" hatası olarak yansıyordu. Artık gerçek bir .pdf dosyası
// üretip jsPDF'in kendi Blob+<a download> mekanizmasıyla indiriyor, bu hiç
// pencere açmadığı için popup engelleyiciyle bir ilgisi yok.
// CV önizlemesini (CvDetailPreview) ekran dışında render edip html2canvas
// ile görüntüye çeviriyoruz; jsPDF ile metin dizmek yerine bunu tercih
// etmemizin sebebi: jsPDF'in standart fontları Türkçe karakterleri (ğ, ş, ı,
// İ) doğru basmıyor, html2canvas ise tarayıcının kendi font render'ını
// kullandığı için bu sorun hiç yaşanmıyor.
// jsPDF + html2canvas ikisi birlikte ~200KB+ gzip, yalnızca indirme
// tıklandığında dinamik import ile yüklenir, ana bundle'ı şişirmez.
async function renderCvNodeToCanvas(data) {
  const { default: html2canvas } = await import('html2canvas');
  const container = document.createElement('div');
  container.style.position = 'fixed';
  container.style.top = '0';
  container.style.left = '-10000px';
  container.style.width = '760px';
  container.style.background = '#ffffff';
  document.body.appendChild(container);

  const root = createRoot(container);
  try {
    await new Promise((resolve) => {
      root.render(<CvDetailPreview data={data} />);
      // Layout + font render'ının oturması için iki animasyon karesi bekle
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
    return await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
  } finally {
    root.unmount();
    container.remove();
  }
}

function sanitizeFileName(name) {
  return String(name || 'CV').replace(/[^\p{L}\p{N}\- ]/gu, '').trim() || 'CV';
}

async function canvasToPdf(canvas) {
  const { default: jsPDF } = await import('jspdf');
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const imgWidth = pageWidth;
  const imgHeight = (canvas.height * imgWidth) / canvas.width;
  const imgData = canvas.toDataURL('image/png');

  let heightLeft = imgHeight;
  let position = 0;
  doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
  heightLeft -= pageHeight;
  while (heightLeft > 0) {
    position -= pageHeight;
    doc.addPage();
    doc.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight;
  }
  return doc;
}

async function downloadCvAsPdf(data, cvName) {
  try {
    let canvas;
    try {
      canvas = await renderCvNodeToCanvas(data);
    } catch {
      // Fotoğraf CORS izni vermiyorsa canvas "tainted" olur ve dışa
      // aktarılamaz, fotoğrafsız (baş harf avatarlı) tekrar dener.
      canvas = await renderCvNodeToCanvas({ ...data, photoUrl: '' });
    }
    const doc = await canvasToPdf(canvas);
    doc.save(`${sanitizeFileName(cvName || data.fullName)}.pdf`);
  } catch {
    alert('CV indirilemedi. Lütfen tekrar deneyin.');
  }
}

// CV önizlemesinde fotoğraf yoksa avatar rozetinde gösterilecek baş harfler
function getInitials(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  const first = parts[0][0] || '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toLocaleUpperCase('tr-TR');
}

function moduleCapitalizeWords(str) {
  if (!str) return str;
  return String(str).trim().split(/\s+/).map(w =>
    w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1).toLocaleLowerCase('tr-TR')
  ).join(' ');
}

// GetCvDetail'in şeması teyit edilmedi (BE dokümantasyonu prose olarak geldi,
// CreateCv'ye giden payload ile aynı camelCase alan adlarını kullandığı
// varsayılıyor), bu yüzden PascalCase'e de düşen defansif bir normalizasyon var,
// projenin geri kalanındaki aynı tuzağa (bkz. AdminCandidatesPage normalizeCandidate) karşı.
function normalizeCvDetail(raw) {
  if (!raw) return null;
  const pick = (camel, pascal) => raw[camel] ?? raw[pascal] ?? '';
  const normExperiences = (list) => (list || []).map((e) => ({
    company: e.company ?? e.Company ?? '',
    title: e.title ?? e.Title ?? e.position ?? e.Position ?? '',
    startDate: e.startDate ?? e.StartDate ?? '',
    endDate: e.endDate ?? e.EndDate ?? '',
    scope: e.scope ?? e.Scope ?? '',
    achievement: e.achievement ?? e.Achievement ?? '',
    challenge: e.challenge ?? e.Challenge ?? '',
    team: e.team ?? e.Team ?? '',
  }));
  const normEducations = (list) => (list || []).map((e) => ({
    school: e.school ?? e.School ?? '',
    degree: e.degree ?? e.Degree ?? '',
    startDate: e.startDate ?? e.StartDate ?? '',
    endDate: e.endDate ?? e.EndDate ?? '',
  }));

  return {
    fullName: pick('fullName', 'FullName'),
    title: pick('title', 'Title'),
    email: pick('email', 'Email'),
    phone: pick('phone', 'Phone'),
    location: pick('location', 'Location'),
    linkedin: raw.linkedin ?? raw.Linkedin ?? raw.LinkedIn ?? '',
    summary: pick('summary', 'Summary'),
    skills: pick('skills', 'Skills'),
    certificates: pick('certificates', 'Certificates'),
    driverLicense: pick('driverLicense', 'DriverLicense'),
    englishLevel: pick('englishLevel', 'EnglishLevel'),
    otherLanguages: pick('otherLanguages', 'OtherLanguages'),
    experiences: normExperiences(raw.experiences ?? raw.Experiences),
    educations: normEducations(raw.educations ?? raw.Educations),
    extra: extractExtraFields(raw),
  };
}

// Sadece görüntüleme amaçlı, salt-okunur CV kağıdı, chat akışındaki canlı
// önizlemeyle aynı CSS sınıflarını (cv-preview__*) kullanır, "yok" cevapları
// gizlenir, editlenebilir state'e bağlı değildir.
function CvDetailPreview({ data }) {
  const skillTags = (data.skills || '').split(/[,،;]+/).map(s => s.trim()).filter(Boolean);
  const hasCertOrLicense = (data.certificates && data.certificates.toLowerCase() !== 'yok') ||
    (data.driverLicense && data.driverLicense.toLowerCase() !== 'yok');
  const hasLanguageInfo = (data.englishLevel && data.englishLevel !== 'none') ||
    (data.otherLanguages && data.otherLanguages.toLowerCase() !== 'yok');
  const extraEntries = Object.entries(data.extra || {});

  const hasLinkedin = data.linkedin && data.linkedin.toLowerCase() !== 'yok';

  return (
    <div className="cv-preview__paper">
      {(data.fullName || data.email || data.phone || data.location) && (
        <div className="cv-preview__personal">
          <div className="cv-preview__avatar">
            {data.photoUrl ? <img src={data.photoUrl} alt="" /> : <span>{getInitials(data.fullName)}</span>}
          </div>
          <div className="cv-preview__personal-text">
            {data.fullName && <h2 className="cv-preview__name">{data.fullName}</h2>}
            {data.title && <p className="cv-preview__headline">{data.title}</p>}
            <div className="cv-preview__contact">
              {data.email && <span><Mail size={12} /> {data.email}</span>}
              {data.phone && <span><Phone size={12} /> {data.phone}</span>}
              {data.location && <span><MapPin size={12} /> {data.location}</span>}
              {hasLinkedin && <span><Link2 size={12} /> {data.linkedin}</span>}
            </div>
          </div>
        </div>
      )}

      {data.summary && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><User size={14} /> Profesyonel Özet</h3>
          <p className="cv-preview__text">{data.summary}</p>
        </div>
      )}

      {data.experiences.length > 0 && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><Briefcase size={14} /> İş Deneyimi</h3>
          {data.experiences.map((exp, i) => (
            <div key={i} className="cv-preview__item">
              <div className="cv-preview__item-header">
                <strong>{moduleCapitalizeWords(exp.title || exp.company)}</strong>
                {exp.startDate && <span className="cv-preview__date">{exp.startDate}{exp.endDate ? ` - ${exp.endDate}` : ''}</span>}
              </div>
              {exp.company && exp.title && <p className="cv-preview__sub">{moduleCapitalizeWords(exp.company)}</p>}
              {exp.scope && exp.scope.toLowerCase() !== 'yok' && <p className="cv-preview__text">{exp.scope}</p>}
              {exp.achievement && <p className="cv-preview__text"><strong>Başarılar:</strong> {exp.achievement}</p>}
              {exp.challenge && exp.challenge.toLowerCase() !== 'yok' && <p className="cv-preview__text"><strong>Zorluklar:</strong> {exp.challenge}</p>}
              {exp.team && exp.team.toLowerCase() !== 'yok' && <p className="cv-preview__text"><strong>Ekip/Bütçe:</strong> {exp.team}</p>}
            </div>
          ))}
        </div>
      )}

      {data.educations.length > 0 && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><GraduationCap size={14} /> Eğitim</h3>
          {data.educations.map((edu, i) => (
            <div key={i} className="cv-preview__item">
              <div className="cv-preview__item-header">
                <strong>{moduleCapitalizeWords(edu.school)}</strong>
                {edu.startDate && <span className="cv-preview__date">{edu.startDate}{edu.endDate ? ` - ${edu.endDate}` : ''}</span>}
              </div>
              {edu.degree && <p className="cv-preview__sub">{moduleCapitalizeWords(edu.degree)}</p>}
            </div>
          ))}
        </div>
      )}

      {skillTags.length > 0 && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><Wrench size={14} /> Yetkinlikler</h3>
          <div className="cv-preview__skills">
            {skillTags.map((skill, i) => (
              <span key={i} className="cv-preview__skill-tag">{skill}</span>
            ))}
          </div>
        </div>
      )}

      {hasCertOrLicense && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><CheckCircle size={14} /> Sertifikalar ve Lisanslar</h3>
          {data.certificates && data.certificates.toLowerCase() !== 'yok' && (
            <p className="cv-preview__text">{moduleCapitalizeWords(data.certificates)}</p>
          )}
          {data.driverLicense && data.driverLicense.toLowerCase() !== 'yok' && (
            <p className="cv-preview__text">Ehliyet: {data.driverLicense.toUpperCase()}</p>
          )}
        </div>
      )}

      {hasLanguageInfo && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><Globe size={14} /> Dil Bilgisi</h3>
          {data.englishLevel && data.englishLevel !== 'none' && (
            <p className="cv-preview__text">{'İngilizce: '}{ENGLISH_LEVEL_LABELS[data.englishLevel] || data.englishLevel}</p>
          )}
          {data.otherLanguages && data.otherLanguages.toLowerCase() !== 'yok' && (
            <p className="cv-preview__text">{'Diğer: '}{moduleCapitalizeWords(data.otherLanguages)}</p>
          )}
        </div>
      )}

      {extraEntries.length > 0 && (
        <div className="cv-preview__section">
          <h3 className="cv-preview__section-title"><Info size={14} /> Ek Bilgiler</h3>
          <div className="cv-preview__extra-grid">
            {extraEntries.map(([k, v]) => (
              <div key={k} className="cv-preview__extra-row">
                <span className="cv-preview__extra-label">{extraFieldLabel(k)}</span>
                <span>{v}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const CvBuilderPage = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('chat');
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [cvList, setCvList] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [downloadingId, setDownloadingId] = useState(null);
  const [viewCvDownloading, setViewCvDownloading] = useState(false);
  const [candidateProfile, setCandidateProfile] = useState(null);

  // CV listesini API'den yükle
  useEffect(() => {
    if (user?.candidateId) {
      getCvList()
        .then(res => {
          if (res.status && res.data) setCvList(res.data);
        })
        .catch(() => {});
      // Görüntüle/İndir'de CV'de eksik kalan kişisel/ATS alanlarını
      // (askerlik durumu, uyruk, cinsiyet vb.) profil bilgisiyle tamamlamak için
      getCandidateProfile().then(setCandidateProfile).catch(() => {});
    }
  }, [user?.candidateId]);

  // Sorular BE'den gelir; uca ulaşılamazsa yerel akış yedek olarak kullanılır
  const [flow, setFlow] = useState(() => buildFlow());
  const [flowReady, setFlowReady] = useState(false);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatTyping, setChatTyping] = useState(false);
  const [chatFinished, setChatFinished] = useState(false);
  const [cvSaved, setCvSaved] = useState(false);
  const [lastSectionId, setLastSectionId] = useState(null);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const [chatStarted, setChatStarted] = useState(false);

  // Metin uzadıkça yatayda kaymak yerine kutu dikeyde büyüsün diye textarea
  // yüksekliği her değişimde içeriğe göre yeniden hesaplanır (CSS max-height'a kadar)
  useEffect(() => {
    const el = chatInputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [chatInput]);

  useEffect(() => {
    let cancelled = false;
    getCvQuestions('Domestic')
      .then((list) => {
        if (cancelled) return;
        const built = buildFlowFromApi(list);
        if (built.length) setFlow(built);
      })
      .catch(() => { /* yerel akışla devam */ })
      .finally(() => { if (!cancelled) setFlowReady(true); });
    return () => { cancelled = true; };
  }, []);

  // Speech-to-text
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);
  // Safari (webkitSpeechRecognition) `continuous: true`'yu düzgün desteklemiyor,
  // kısa bir sessizlikte bile onend tetikleyip dinlemeyi kesiyor, buton "aktif"ten
  // hemen "pasif"e dönüyor. Kullanıcı elle durdurmadığı sürece bu ref true kalır ve
  // onend'de otomatik olarak yeniden başlatılır.
  const shouldListenRef = useRef(false);

  const speechSupported = typeof window !== 'undefined' && ('SpeechRecognition' in window || 'webkitSpeechRecognition' in window);

  const startRecognition = () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = 'tr-TR';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = 0; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      setChatInput(transcript);
    };

    recognition.onend = () => {
      if (shouldListenRef.current) {
        // Safari'de aynı örneği tekrar başlatmak InvalidStateError verebiliyor,
        // her yeniden başlatmada yeni bir tanıma nesnesi kuruluyor
        startRecognition();
      } else {
        setIsListening(false);
      }
    };

    recognition.onerror = (e) => {
      if (e.error === 'not-allowed' || e.error === 'service-not-allowed') {
        shouldListenRef.current = false;
        setIsListening(false);
        setChatMessages(prev => [...prev, {
          role: 'ai', text: 'Mikrofon erişimi reddedildi. Lütfen tarayıcı ayarlarından mikrofon iznini verin.',
        }]);
      }
      // 'no-speech' / 'aborted' gibi geçici hatalarda ardından onend tetiklenir
      // ve şart hâlâ geçerliyse otomatik yeniden başlatılır
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const toggleListening = () => {
    if (isListening) {
      shouldListenRef.current = false;
      recognitionRef.current?.stop();
      setIsListening(false);
      return;
    }

    if (!speechSupported) return;

    shouldListenRef.current = true;
    setIsListening(true);
    startRecognition();
  };

  // Experience/education accumulators
  const [experiences, setExperiences] = useState([]);
  const [educations, setEducations] = useState([]);
  // Temp buffers for current experience/education being built
  const expBufferRef = useRef({});
  const eduBufferRef = useRef({});

  // Upload in chat
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadParsed, setUploadParsed] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const chatFileRef = useRef(null);

  // Manual form
  const [cvName, setCvName] = useState('');
  const [form, setForm] = useState({
    fullName: '', email: '', phone: '', location: '', summary: '',
    experiences: [{ ...emptyExperience }],
    educations: [{ ...emptyEducation }],
    skills: '',
  });

  const resetForm = () => {
    setForm({ fullName: '', email: '', phone: '', location: '', summary: '', experiences: [{ ...emptyExperience }], educations: [{ ...emptyEducation }], skills: '' });
    setCvName('');
  };

  const resetChat = () => {
    setCurrentIdx(0);
    setAnswers({});
    setChatMessages([]);
    setChatInput('');
    setChatTyping(false);
    setChatFinished(false);
    setCvSaved(false);
    setLastSectionId(null);
    setChatStarted(false);
    setExperiences([]);
    setEducations([]);
    setUploadFile(null);
    setUploadParsed(null);
    setUploadError('');
  };

  const updateField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));
  const updateListItem = (list, index, field, value) => {
    setForm(prev => ({ ...prev, [list]: prev[list].map((item, i) => i === index ? { ...item, [field]: value } : item) }));
  };
  const addListItem = (list, empty) => setForm(prev => ({ ...prev, [list]: [...prev[list], { ...empty }] }));
  const removeListItem = (list, index) => setForm(prev => ({ ...prev, [list]: prev[list].filter((_, i) => i !== index) }));

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    // Her mesajdan sonra input'a focus
    setTimeout(() => chatInputRef.current?.focus(), 100);
  }, [chatMessages, chatTyping]);

  // Get active (visible) questions based on current answers
  const getActiveFlow = (currentAnswers) => {
    return getActiveQuestions(flow, currentAnswers);
  };

  // Find next question index in the active flow
  const findNextActiveIndex = (afterIdx, currentAnswers) => {
    const activeFlow = getActiveQuestions(flow, currentAnswers);
    const currentQ = flow[afterIdx];
    if (!currentQ) return -1;
    const activeIdx = activeFlow.findIndex(q => q.id === currentQ.id);
    if (activeIdx === -1 || activeIdx + 1 >= activeFlow.length) return -1;
    const nextQ = activeFlow[activeIdx + 1];
    return flow.findIndex(q => q.id === nextQ.id);
  };

  // Start chat flow
  const startChat = () => {
    if (chatStarted) return;
    setChatStarted(true);
    const firstQ = flow[0];
    if (!firstQ) return;
    // API sorusunda bölüm adı doğrudan soruda gelir; yerel akışta SECTIONS'tan bulunur
    const section = firstQ.section
      ? { id: firstQ.section, title: firstQ.section }
      : getCurrentSection(firstQ.id);
    const msgs = [];
    if (section) {
      msgs.push({ role: 'section', text: section.title, sectionId: section.id });
      setLastSectionId(section.id);
    }
    msgs.push({ role: 'ai', text: firstQ.text, questionId: firstQ.id, type: firstQ.type, choices: firstQ.choices });
    setChatMessages(msgs);
  };

  // Sayfa yeniden açıldığında daha önce kaydedilen (kök/tekrarlanmayan) cevapları
  // geri yükleyip kaldığı yerden devam ettirir, bkz. handleAnswer'daki autosave.
  // Tekrarlanabilir grup soruları (deneyim/eğitim) kaydedilmediği için buradan da
  // geri yüklenmez; akış onlara geldiğinde normal şekilde yeniden sorulur.
  const resumeOrStartChat = async () => {
    if (chatStarted) return;
    setChatStarted(true);

    if (!user?.candidateId) { startChat(); return; }

    const savedMap = {};
    try {
      const res = await getCandidateAnswers(user.candidateId);
      (res?.data || []).forEach((x) => {
        if (x && String(x.scope || '').toLowerCase() === 'domestic' && x.key && x.value) {
          savedMap[x.key] = x.value;
        }
      });
    } catch { /* sunucudan alınamazsa sıfırdan başla */ }

    const restoredAnswers = {};
    flow.forEach((q) => {
      if (q.groupKey || q.fieldKey === undefined) return; // yalnızca kök BE alanları geri yüklenir
      const fk = resolveFieldKey(q);
      if (fk && savedMap[fk] !== undefined) restoredAnswers[q.field] = savedMap[fk];
    });

    if (!Object.keys(restoredAnswers).length) { startChat(); return; }

    setAnswers(restoredAnswers);

    const activeFlow = getActiveQuestions(flow, restoredAnswers);
    const history = [];
    let lastSection = null;
    let nextQ = null;
    for (let i = 0; i < activeFlow.length; i++) {
      const q = activeFlow[i];
      if (restoredAnswers[q.field] === undefined) { nextQ = q; break; }
      if (q.section && q.section !== lastSection) {
        history.push({ role: 'section', text: q.section, sectionId: q.section });
        lastSection = q.section;
      }
      history.push({ role: 'ai', text: q.text, questionId: q.id, type: q.type, choices: q.choices });
      history.push({ role: 'user', text: restoredAnswers[q.field] });
    }

    history.push({ role: 'ai', text: 'Tekrar hoş geldin! Kaldığın yerden devam ediyoruz.' });

    if (!nextQ) {
      // Tüm kök sorular cevaplanmış; akışta kalan (ör. deneyim) sorular varsa
      // findNextActiveIndex zaten aynı mantıkla bir sonrakini bulacaktır
      setLastSectionId(lastSection);
      setChatMessages(history);
      const lastAnsweredIdx = flow.findIndex((q) => q.id === activeFlow[activeFlow.length - 1]?.id);
      advanceToNext(lastAnsweredIdx === -1 ? 0 : lastAnsweredIdx, restoredAnswers);
      return;
    }

    if (nextQ.section && nextQ.section !== lastSection) {
      history.push({ role: 'section', text: nextQ.section, sectionId: nextQ.section });
      lastSection = nextQ.section;
    }
    history.push({ role: 'ai', text: nextQ.text, questionId: nextQ.id, type: nextQ.type, choices: nextQ.choices });
    setLastSectionId(lastSection);
    setCurrentIdx(flow.findIndex((q) => q.id === nextQ.id));
    setChatMessages(history);
  };

  // Auto-start chat when tab is active
  useEffect(() => {
    if (activeTab === 'chat' && !chatStarted && flowReady && flow.length > 0) {
      resumeOrStartChat();
    }
    // flowReady: sorular BE'den geldiğinde sohbet o an başlamalı
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, flowReady, flow]);

  // Add section divider if needed
  const maybeSectionDivider = (prevQId, nextQId) => {
    // API akışında bölüm bilgisi sorunun kendisinde
    const prevQ = flow.find((q) => q.id === prevQId);
    const nextQ = flow.find((q) => q.id === nextQId);
    if (nextQ?.section) {
      if (prevQ?.section === nextQ.section || nextQ.section === lastSectionId) return null;
      setLastSectionId(nextQ.section);
      return { role: 'section', text: nextQ.section, sectionId: nextQ.section };
    }

    if (didSectionChange(prevQId, nextQId)) {
      const section = getCurrentSection(nextQId);
      if (section && section.id !== lastSectionId) {
        setLastSectionId(section.id);
        return { role: 'section', text: section.title, sectionId: section.id };
      }
    }
    return null;
  };

  // Advance to next question
  const advanceToNext = (fromIdx, newAnswers) => {
    const nextIdx = findNextActiveIndex(fromIdx, newAnswers);
    if (nextIdx === -1 || nextIdx >= flow.length) {
      // Tüm sorular bitti
      setChatFinished(true);
      setTimeout(() => {
        setChatMessages(prev => [...prev, {
          role: 'ai',
          text: 'Tebrikler! CV\'niz hazır. Yukarıdaki CV Kaydet butonundan kaydedebilirsiniz.',
          questionId: '__done',
        }]);
        setChatTyping(false);
      }, 600);
      return;
    }

    const nextQ = flow[nextIdx];
    setCurrentIdx(nextIdx);

    setTimeout(() => {
      const newMsgs = [];
      const divider = maybeSectionDivider(flow[fromIdx]?.id, nextQ.id);
      if (divider) newMsgs.push(divider);
      newMsgs.push({ role: 'ai', text: nextQ.text, questionId: nextQ.id, type: nextQ.type, choices: nextQ.choices });
      setChatMessages(prev => [...prev, ...newMsgs]);
      setChatTyping(false);
    }, 600);
  };

  // Auto-format helpers
  const capitalizeWords = (str) => {
    return str.trim().split(/\s+/).map(w =>
      w.charAt(0).toLocaleUpperCase('tr-TR') + w.slice(1).toLocaleLowerCase('tr-TR')
    ).join(' ');
  };

  const formatPhone = (str) => {
    let digits = str.replace(/[^0-9]/g, '');
    // 05xx → +905xx
    if (digits.startsWith('0') && digits.length >= 10) {
      digits = '90' + digits.slice(1);
    }
    return '+' + digits;
  };

  // Validation rules per field
  const validateField = (fieldId, value) => {
    switch (fieldId) {
      case 'fullName': {
        const cleaned = value.replace(/[^a-zA-ZçÇğĞıİöÖşŞüÜâÂîÎûÛ\s]/g, '');
        if (cleaned.trim().split(/\s+/).length < 2) return 'Lütfen adınızı ve soyadınızı yazın. (Örn: Ahmet Yılmaz)';
        return null;
      }
      case 'phone': {
        const digits = value.replace(/[^0-9]/g, '');
        if (digits.length < 10) return 'Telefon numarası çok kısa. Lütfen alan koduyla birlikte yazın. (Örn: +90 532 123 4567)';
        if (digits.length > 15) return 'Telefon numarası çok uzun. Lütfen kontrol edin.';
        return null;
      }
      case 'email': {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Geçerli bir e-posta adresi girin. (Örn: isim@email.com)';
        return null;
      }
      case 'birthDate': {
        if (isSkipAnswer(value)) return null;
        // Ayraç olarak /, ., - veya boşluk kabul edilir (23/04/1983, 23.04.1983, 23 04 1983 hepsi geçerli)
        const dateMatch = value.trim().match(/^(\d{1,2})[./\-\s]+(\d{1,2})[./\-\s]+(\d{4})$/);
        if (!dateMatch) return 'Doğum tarihinizi Gün/Ay/Yıl formatında yazın. (Örn: 15/03/1990 veya 15 03 1990)';
        const [, d, m, y] = dateMatch.map(Number);
        if (m < 1 || m > 12 || d < 1 || d > 31) return 'Geçerli bir tarih girin. (Örn: 15/03/1990)';
        const age = new Date().getFullYear() - y;
        if (age < 16 || age > 80) return 'Doğum yılını kontrol edin. Yaşınız 16-80 arasında olmalıdır.';
        return null;
      }
      default:
        return null;
    }
  };

  // Auto-format value before saving
  const formatValue = (fieldId, value) => {
    switch (fieldId) {
      case 'fullName': return capitalizeWords(value);
      case 'phone': return formatPhone(value);
      case 'email': return value.trim().toLowerCase();
      case 'location': return capitalizeWords(value);
      case 'birthDate': {
        if (isSkipAnswer(value)) return value.trim();
        // Hangi ayraçla girilirse girilsin (boşluk, nokta, tire) kayıtta tek tip DD/MM/YYYY'ye çevrilir
        const m = value.trim().match(/^(\d{1,2})[./\-\s]+(\d{1,2})[./\-\s]+(\d{4})$/);
        if (!m) return value.trim();
        const [, d, mo, y] = m;
        return `${d.padStart(2, '0')}/${mo.padStart(2, '0')}/${y}`;
      }
      case '_expInfo': return capitalizeWords(value);
      case '_eduInfo': return capitalizeWords(value);
      case 'driverLicense': return value.trim().toUpperCase();
      case 'otherLanguages': return capitalizeWords(value);
      case 'nationality': return capitalizeWords(value);
      case 'certificates': return value.trim();
      default: return value.trim();
    }
  };

  // Handle user answer (text or choice)
  const handleAnswer = (text, choiceValue) => {
    const currentQ = flow[currentIdx];
    if (!currentQ) return;

    const displayText = choiceValue
      ? currentQ.choices?.find(c => c.value === choiceValue)?.label || text
      : text;

    // Validate before accepting (skip for choice questions)
    if (!choiceValue) {
      const error = validateField(currentQ.field, text.trim());
      if (error) {
        setChatMessages(prev => [...prev,
          { role: 'user', text: displayText },
          { role: 'ai', text: error, questionId: currentQ.id, type: currentQ.type, choices: currentQ.choices },
        ]);
        return;
      }
    }

    // Format the value
    const formatted = choiceValue || formatValue(currentQ.field, text.trim());
    const displayFormatted = choiceValue ? displayText : formatted;

    setChatMessages(prev => [...prev, { role: 'user', text: displayFormatted }]);
    setChatTyping(true);

    const value = formatted;
    const newAnswers = { ...answers, [currentQ.field]: value };

    // Accumulate experience: exp_info → parse smart
    if (currentQ.id === 'exp_info') {
      const newExp = parseExpInput(value);
      setExperiences(prev => [...prev, newExp]);
      expBufferRef.current = {};
    }
    if (currentQ.id === 'exp_detail') {
      // Son deneyimi güncelle
      setExperiences(prev => {
        const updated = [...prev];
        if (updated.length > 0) {
          updated[updated.length - 1] = { ...updated[updated.length - 1], scope: value };
        }
        return updated;
      });
      expBufferRef.current = {};
    }

    // Accumulate education: edu_info (school+degree+dates)
    if (currentQ.id === 'edu_info') {
      // Tarihleri çıkar
      const allDates = value.match(/\d{4}/g) || [];
      // Tarihleri metinden temizle
      let cleaned = value.replace(/\d{4}\s*[-–—]\s*\d{4}/g, '').replace(/\d{4}/g, '');
      // Virgül veya tire ile ayır (üniversite adındaki tireleri korumak için sadece virgül + son tire)
      const segments = cleaned.split(/[,]/).map(s => s.trim()).filter(Boolean);
      const newEdu = {
        school: segments[0] || cleaned.trim() || value,
        degree: segments.slice(1).join(', ').trim() || '',
        startDate: allDates[0] || '',
        endDate: allDates[1] || '',
      };
      setEducations(prev => [...prev, newEdu]);
      eduBufferRef.current = {};
    }

    setAnswers(newAnswers);

    // Sayfa yenilenince/kapanınca ilerlemenin kaybolmaması için her kök (tekrarlanmayan)
    // BE sorusu anında kaydedilir, mekanizma AbroadPage'deki ile aynı. Tekrarlanabilir
    // grup soruları (deneyim/eğitim) burada kasıtlı atlanır: SaveCandidateAnswers key
    // başına tek satır tutuyor, ikinci bir deneyim girildiğinde aynı key'in üzerine
    // yazılır (bkz. applyFieldKey'deki counters index=0 sorunu), o düzeltilmeden
    // buradan da kaydetmek yanıltıcı olur.
    if (currentQ.fieldKey !== undefined && !currentQ.groupKey) {
      const fieldKey = resolveFieldKey(currentQ);
      if (fieldKey) saveCandidateAnswers([{ key: fieldKey, value }]).catch(() => {});
    }

    // Handle loop (exp_more / edu_more)
    if (currentQ.loop && value === currentQ.loop.onValue) {
      const jumpToIdx = flow.findIndex(q => q.id === currentQ.loop.jumpTo);
      if (jumpToIdx !== -1) {
        setCurrentIdx(jumpToIdx);
        const jumpQ = flow[jumpToIdx];
        setTimeout(() => {
          const newMsgs = [];
          const divider = maybeSectionDivider(currentQ.id, jumpQ.id);
          if (divider) newMsgs.push(divider);
          newMsgs.push({ role: 'ai', text: jumpQ.text, questionId: jumpQ.id, type: jumpQ.type, choices: jumpQ.choices });
          setChatMessages(prev => [...prev, ...newMsgs]);
          setChatTyping(false);
        }, 600);
        return;
      }
    }

    advanceToNext(currentIdx, newAnswers);
  };

  const handleChatSend = () => {
    const text = chatInput.trim();
    if (!text || chatTyping || chatFinished) return;
    setChatInput('');
    handleAnswer(text, null);
  };

  const handleChoiceClick = (choiceValue) => {
    if (chatTyping || chatFinished) return;
    handleAnswer(choiceValue, choiceValue);
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleChatSend(); }
  };

  const handleChatSave = async () => {
    setGenerating(true);
    try {
      const cvName = answers.cvName || `${answers.fullName || 'CV'} - ${new Date().toLocaleDateString('tr-TR')}`;
      // Payload fieldKey ile üretilir; alan adları hard-code edilmez.
      // "yok/geç" gibi cevaplar cleanAnswer ile ayıklanır, CV'ye yazılmaz.
      const payload = { name: cvName, source: uploadParsed ? 'upload' : 'chat' };

      const apiDriven = flow.some((q) => q.fieldKey);
      if (apiDriven) {
        // Tekrarlanabilir gruplarda kaçıncı kayıt olduğunu takip et
        const counters = {};
        flow.forEach((q) => {
          if (q.generated) return;
          const value = answers[q.field];
          if (value === undefined) return;
          const fieldKey = resolveFieldKey(q);
          if (!fieldKey) return;
          const group = q.groupKey;
          const index = group ? (counters[group] || 0) : 0;
          applyFieldKey(payload, fieldKey, value, index);
        });

        // Sohbette toplanan tekrarlı kayıtlar (deneyim/eğitim listeleri)
        if (experiences.length) {
          payload.experiences = experiences.map((exp) => {
            const out = {};
            Object.keys(exp).forEach((k) => {
              const v = cleanAnswer(exp[k]);
              if (v) out[k] = v;
            });
            return out;
          }).filter((e) => Object.keys(e).length);
        }
        if (educations.length) {
          payload.educations = educations.map((edu) => {
            const out = {};
            Object.keys(edu).forEach((k) => {
              const v = cleanAnswer(edu[k]);
              if (v) out[k] = v;
            });
            return out;
          }).filter((e) => Object.keys(e).length);
        }
      } else {
        // Yerel akış yedeği
        Object.assign(payload, {
          fullName: cleanAnswer(answers.fullName),
          title: cleanAnswer(answers.title),
          email: cleanAnswer(answers.email),
          phone: cleanAnswer(answers.phone),
          location: cleanAnswer(answers.location),
          linkedin: cleanAnswer(answers.linkedin),
          birthDate: cleanAnswer(answers.birthDate),
          maritalStatus: cleanAnswer(answers.maritalStatus),
          militaryStatus: cleanAnswer(answers.militaryStatus),
          summary: cleanAnswer(answers.summary),
          skills: cleanAnswer(answers.skills),
          certificates: cleanAnswer(answers.certificates),
          driverLicense: cleanAnswer(answers.driverLicense),
          englishLevel: cleanAnswer(answers.englishLevel),
          otherLanguages: cleanAnswer(answers.otherLanguages),
          experiences: experiences.map((exp) => ({
            company: cleanAnswer(exp.company),
            title: cleanAnswer(exp.title),
            startDate: cleanAnswer(exp.startDate),
            endDate: cleanAnswer(exp.endDate),
            scope: cleanAnswer(exp.scope),
            achievement: cleanAnswer(exp.achievement),
            challenge: cleanAnswer(exp.challenge),
            team: cleanAnswer(exp.team),
          })),
          educations: educations.map((edu) => ({
            school: cleanAnswer(edu.school),
            degree: cleanAnswer(edu.degree),
            startDate: cleanAnswer(edu.startDate),
            endDate: cleanAnswer(edu.endDate),
          })),
        });
      }

      // Boş alanları hiç gönderme
      Object.keys(payload).forEach((k) => {
        if (payload[k] === '' || payload[k] === null) delete payload[k];
      });

      if (!user?.candidateId) {
        alert('CV kaydetmek için önce profilinizi oluşturmanız gerekiyor. Profilim sayfasından profil oluşturun.');
        setGenerating(false);
        return;
      }

      const result = await createCv(payload);
      console.log('[CreateCv] Result:', result);
      const savedId = (result.status && result.data) ? result.data : Date.now();
      const newCv = {
        id: savedId,
        name: cvName,
        source: payload.source,
        fullName: payload.fullName || '',
        location: payload.location || '',
        createdAt: new Date().toISOString(),
      };
      setCvList(prev => [newCv, ...prev]);
      setCvSaved(true);
      setTimeout(() => setCreateModalOpen(false), 1200);
    } catch (e) {
      console.error('[CreateCv] Error:', e);
      alert('CV kaydedilemedi. Lütfen tekrar deneyin.');
    } finally {
      setGenerating(false);
    }
  };

  // Upload within chat tab
  const handleUploadParse = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadError('');
    setUploadParsed(null);
    try {
      const data = await cvAutofill(uploadFile);
      const raw = data.raw || data;
      console.log('[CvUpload] Parsed data:', data);
      console.log('[CvUpload] Raw data keys:', Object.keys(raw));
      setUploadParsed(data);

      // Answers, tüm mevcut alanları doldur
      const newAnswers = { ...answers };
      if (data.fullName || data.name) {
        newAnswers.fullName = data.fullName || [data.name, data.surname].filter(Boolean).join(' ');
      }
      if (data.email) newAnswers.email = data.email;
      if (data.phone) newAnswers.phone = data.phone;
      if (data.currentLocation) newAnswers.location = data.currentLocation;
      if (data.summary) newAnswers.summary = data.summary;
      if (data.title) newAnswers.title = data.title;
      // BE, dönen alan adını tutarlı vermiyor (birthdate/dateOfBirth/birthDate),
      // cvAutofill() üçünü de dener; burada akışın beklediği camelCase anahtara yazılır
      if (data.birthdate) newAnswers.birthDate = data.birthdate;
      if (data.militaryStatus) newAnswers.militaryStatus = data.militaryStatus;
      if (data.hobbies) newAnswers.sportsHobbies = Array.isArray(data.hobbies) ? data.hobbies.join(', ') : String(data.hobbies);

      // Skills, string veya array
      const skills = data.skills || raw.skills;
      if (skills) {
        newAnswers.skills = Array.isArray(skills) ? skills.join(', ') : String(skills);
      }

      // Sertifikalar
      const certs = data.certificates || raw.certificates || raw.certifications;
      if (certs) {
        newAnswers.certificates = Array.isArray(certs) ? certs.join(', ') : String(certs);
      }

      // Dil bilgisi
      const langs = data.languages || raw.languages;
      if (langs) {
        if (Array.isArray(langs)) {
          const eng = langs.find(l => /english|ingilizce/i.test(l.name || l.language || l));
          if (eng) {
            const level = eng.level || eng.proficiency || '';
            if (/native|fluent|akıcı/i.test(level)) newAnswers.englishLevel = 'fluent';
            else if (/advanced|ileri|c1|c2/i.test(level)) newAnswers.englishLevel = 'advanced';
            else if (/intermediate|orta|b1|b2/i.test(level)) newAnswers.englishLevel = 'intermediate';
            else if (/basic|temel|a1|a2/i.test(level)) newAnswers.englishLevel = 'basic';
          }
          const others = langs.filter(l => !/english|ingilizce/i.test(l.name || l.language || l));
          if (others.length) {
            newAnswers.otherLanguages = others.map(l => typeof l === 'string' ? l : `${l.name || l.language} - ${l.level || ''}`).join(', ');
          }
        }
      }

      // Ehliyet
      if (raw.driverLicense || raw.drivingLicense) {
        newAnswers.driverLicense = raw.driverLicense || raw.drivingLicense;
      }

      setAnswers(newAnswers);

      // Deneyimler, birçok olası format
      const exps = data.experiences || raw.experiences || raw.workExperience || raw.workHistory || raw.jobs;
      if (exps && Array.isArray(exps) && exps.length > 0) {
        setExperiences(exps.map(exp => ({
          company: exp.company || exp.companyName || exp.employer || exp.organization || '',
          title: exp.title || exp.position || exp.role || exp.jobTitle || '',
          startDate: exp.startDate || exp.start || exp.from || '',
          endDate: exp.endDate || exp.end || exp.to || (exp.current ? 'Devam' : ''),
          scope: exp.description || exp.scope || exp.responsibilities || exp.duties || '',
          achievement: exp.achievement || exp.achievements || '',
          team: exp.team || '',
        })));
      }

      // Eğitimler
      const edus = data.educations || raw.educations || raw.educationHistory || raw.schools;
      if (edus && Array.isArray(edus) && edus.length > 0) {
        setEducations(edus.map(edu => {
          const degree = [edu.degree, edu.department].filter(Boolean).join(' - ');
          return {
            school: edu.school || edu.institution || edu.university || edu.name || '',
            degree: degree || edu.field || edu.major || '',
            startDate: edu.startDate || edu.start || edu.from || '',
            endDate: edu.endDate || edu.end || edu.to || edu.graduationYear || '',
          };
        }));
      }

      // Direkt gelen alanlar
      if (raw.englishLevel) newAnswers.englishLevel = raw.englishLevel;
      if (raw.driverLicense) newAnswers.driverLicense = raw.driverLicense;
      if (raw.otherLanguages) newAnswers.otherLanguages = raw.otherLanguages;

      // CV'den alınan bilgileri mesajla özetle
      const filled = [];
      if (newAnswers.fullName) filled.push('ad soyad');
      if (newAnswers.phone) filled.push('telefon');
      if (newAnswers.location) filled.push('konum');
      if (newAnswers.title) filled.push('pozisyon');
      const hasExps = exps && Array.isArray(exps) && exps.length > 0;
      const hasEdus = edus && Array.isArray(edus) && edus.length > 0;
      if (hasExps) filled.push(`${exps.length} iş deneyimi`);
      if (hasEdus) filled.push(`${edus.length} eğitim`);
      if (newAnswers.skills) filled.push('beceriler');

      const filledText = filled.length > 0 ? `CV'nizden ${filled.join(', ')} bilgilerini aldım.` : 'CV\'niz analiz edildi.';

      // Eksik olan ilk soruyu bul ve chat'i o sorudan devam ettir
      const activeFlow = getActiveQuestions(flow, newAnswers);
      let nextQuestionIdx = -1;
      for (let i = 0; i < activeFlow.length; i++) {
        const q = activeFlow[i];
        // Deneyim/eğitim soruları, upload'dan geldiyse atla. Yerel akışta sabit
        // id'lerle (exp_info/exp_detail/exp_more/edu_info/edu_more), BE-driven
        // akışta ise groupKey ile ('experience'/'education') gelir, BE tarafı
        // yeni alan eklerse id bazlı kontrol yakalamaz, bu yüzden ikisi de kontrol edilir.
        const isExpQuestion = ['exp_info', 'exp_detail', 'exp_more', 'experience_more'].includes(q.id) || q.groupKey === 'experience';
        const isEduQuestion = ['edu_info', 'edu_more', 'education_more'].includes(q.id) || q.groupKey === 'education';
        if (isExpQuestion && hasExps) continue;
        if (isEduQuestion && hasEdus) continue;
        if (!newAnswers[q.field] && !q.field.startsWith('_')) {
          // Bu soru henüz cevaplanmamış
          nextQuestionIdx = flow.findIndex(fq => fq.id === q.id);
          break;
        }
        if (!newAnswers[q.field] && q.field.startsWith('_') && q.id !== 'exp_info' && q.id !== 'edu_info') {
          nextQuestionIdx = flow.findIndex(fq => fq.id === q.id);
          break;
        }
      }

      // Eğer eksik soru varsa, mesaj + soru sor
      if (nextQuestionIdx >= 0) {
        const nextQ = flow[nextQuestionIdx];
        setCurrentIdx(nextQuestionIdx);
        const section = getCurrentSection(nextQ.id);
        const msgs = [{ role: 'ai', text: filledText + '\n\nŞimdi eksik bilgileri tamamlayalım.' }];
        if (section) msgs.push({ role: 'section', text: section.title, sectionId: section.id });
        msgs.push({ role: 'ai', text: nextQ.text, questionId: nextQ.id, type: nextQ.type, choices: nextQ.choices });
        setChatMessages(prev => [...prev, ...msgs]);
      } else {
        // Tüm bilgiler tam
        setChatMessages(prev => [...prev,
          { role: 'ai', text: filledText + ' Tüm bilgiler eksiksiz alındı! Yukarıdan CV\'nizi kaydedebilirsiniz.' }
        ]);
      }
    } catch {
      setUploadError('CV analiz edilemedi. Lütfen tekrar deneyin.');
    } finally {
      setUploading(false);
    }
  };

  // Manual form
  const handleGenerate = async () => {
    setGenerating(true);
    try {
      await new Promise(r => setTimeout(r, 2000));
      const newCv = {
        id: Date.now(),
        name: cvName || `${form.fullName || 'CV'} - ${new Date().toLocaleDateString('tr-TR')}`,
        title: form.experiences[0]?.title || '',
        location: form.location,
        createdAt: new Date().toISOString(),
        source: 'manual',
      };
      setCvList(prev => [newCv, ...prev]);
      resetForm();
      setCreateModalOpen(false);
    } catch {
      alert('CV oluşturulamadı.');
    } finally {
      setGenerating(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteCv(id);
      setCvList(prev => prev.filter(cv => cv.id !== id));
    } catch {
      setCvList(prev => prev.filter(cv => cv.id !== id));
    }
  };

  // CV detay modalı, GetCvDetail
  const [viewCv, setViewCv] = useState(null); // { name, loading, error, data }

  const handleViewCv = async (cv) => {
    setViewCv({ name: cv.name, loading: true, error: '', data: null });
    try {
      const res = await getCvDetail(cv.id);
      if (!res.status || !res.data) {
        setViewCv({ name: cv.name, loading: false, error: res.message || 'CV detayı alınamadı.', data: null });
        return;
      }
      setViewCv({ name: cv.name, loading: false, error: '', data: mergeWithCandidateProfile(normalizeCvDetail(res.data), candidateProfile) });
    } catch {
      setViewCv({ name: cv.name, loading: false, error: 'Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.', data: null });
    }
  };

  const closeViewCv = () => setViewCv(null);

  const handleDownloadCv = async (cv) => {
    setDownloadingId(cv.id);
    try {
      const res = await getCvDetail(cv.id);
      if (!res.status || !res.data) {
        alert(res.message || 'CV indirilemedi.');
        return;
      }
      await downloadCvAsPdf(mergeWithCandidateProfile(normalizeCvDetail(res.data), candidateProfile), cv.name);
    } catch {
      alert('Sunucuya ulaşılamadı. Bağlantınızı kontrol edin.');
    } finally {
      setDownloadingId(null);
    }
  };

  const formatDate = (dateStr) => {
    try { return new Date(dateStr).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short', year: 'numeric' }); }
    catch { return dateStr; }
  };

  const hasCvContent = answers.fullName || experiences.length > 0 || educations.length > 0 || answers.skills;

  // CV is saveable once cv_name is answered
  const cvSaveable = isCvPhaseComplete(answers) || !!uploadParsed;

  // Progress calculation
  const activeFlow = chatStarted ? getActiveFlow(answers) : [];
  const currentActiveIdx = activeFlow.findIndex(q => q.id === flow[currentIdx]?.id);
  const progress = getProgress(currentActiveIdx >= 0 ? currentActiveIdx : 0, activeFlow.length);

  // Current question for choice rendering
  const currentQuestion = flow[currentIdx];
  const isChoiceQuestion = currentQuestion?.type === 'choice' && !chatFinished && !chatTyping;

  return (
    <div className="cv-builder-page">
      <div className="cv-builder__container">
        <BackButton />

        <div className="cv-builder__header">
          <PenTool size={24} />
          <div>
            <h1 className="cv-builder__title">CV'lerim</h1>
            <p className="cv-builder__subtitle">Yapay zeka ile yazışarak veya manuel olarak CV oluştur.</p>
          </div>
          <button
            className="cv-builder__create-cta"
            onClick={() => { setActiveTab('chat'); setCreateModalOpen(true); }}
          >
            <Sparkles size={16} /> Yapay Zeka ile CV Oluştur
          </button>
        </div>

        {/* CV Listesi, ana görünüm */}
        {cvList.length === 0 ? (
          <div className="cv-builder__empty">
            <FileText size={40} />
            <h3>Henüz CV'n yok</h3>
            <p>Yapay zeka ile yazışarak veya manuel olarak CV oluşturabilirsin.</p>
            <button
              className="cv-builder__create-cta"
              onClick={() => { setActiveTab('chat'); setCreateModalOpen(true); }}
            >
              <Sparkles size={16} /> Yapay Zeka ile CV Oluştur
            </button>
          </div>
        ) : (
          <div className="cv-builder__grid">
            {cvList.map(cv => (
              <div key={cv.id} className="cv-builder__cv-card">
                <div className="cv-builder__cv-icon">
                  <FileText size={20} />
                </div>
                <div className="cv-builder__cv-info">
                  <h3 className="cv-builder__cv-name">{cv.name}</h3>
                  <div className="cv-builder__cv-meta">
                    {cv.title && <span>{cv.title}</span>}
                    {cv.location && <span>{cv.location}</span>}
                    <span>{formatDate(cv.createdAt)}</span>
                  </div>
                  <span className={`cv-builder__cv-source cv-builder__cv-source--${cv.source}`}>
                    {cv.source === 'upload' ? 'Yüklenen' : cv.source === 'chat' ? 'Yapay Zeka' : 'Manuel'}
                  </span>
                </div>
                <div className="cv-builder__cv-actions">
                  <button className="cv-builder__cv-action" title="Görüntüle" onClick={() => handleViewCv(cv)}><Eye size={14} /></button>
                  <button className="cv-builder__cv-action" title="İndir" onClick={() => handleDownloadCv(cv)} disabled={downloadingId === cv.id}>
                    {downloadingId === cv.id ? <Loader2 size={14} className="cv-builder__spinner" /> : <Download size={14} />}
                  </button>
                  <button className="cv-builder__cv-action cv-builder__cv-action--danger" title="Sil" onClick={() => handleDelete(cv.id)}>
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── CV Oluşturma Modalı, içinde Akıllı Asistan / Manuel sekmeleri ── */}
      {createModalOpen && ReactDOM.createPortal(
        <div className="cv-create-overlay" onClick={(e) => { if (e.target === e.currentTarget) setCreateModalOpen(false); }}>
          <div className="cv-create-modal">
            <div className="cv-create-modal__header">
              <div className="cv-builder__tabs">
                <button className={`cv-builder__tab ${activeTab === 'chat' ? 'cv-builder__tab--active' : ''}`} onClick={() => setActiveTab('chat')}>
                  <MessageSquare size={16} /> Akıllı CV Asistanı
                </button>
                <button className={`cv-builder__tab ${activeTab === 'manual' ? 'cv-builder__tab--active' : ''}`} onClick={() => setActiveTab('manual')}>
                  <PenTool size={16} /> Manuel Oluştur
                </button>
              </div>
              <button className="cv-create-modal__close" onClick={() => setCreateModalOpen(false)}><X size={20} /></button>
            </div>
            <div className="cv-create-modal__body">
        {/* ── TAB: Chat ── */}
        {activeTab === 'chat' && (
          <>
                {/* Progress Bar */}
                <div className="cv-progress">
                  <div className="cv-progress__bar">
                    <div className="cv-progress__fill" style={{ width: `${chatFinished ? 100 : progress}%` }} />
                  </div>
                  <div className="cv-progress__info">
                    <span className="cv-progress__label">
                      {chatFinished ? 'Tamamlandı' : getCurrentSection(flow[currentIdx]?.id)?.title || ''}
                    </span>
                    <span className="cv-progress__pct">{chatFinished ? 100 : progress}%</span>
                  </div>
                </div>

                {/* CV Preview, ÜSTTE */}
                {hasCvContent && (
                  <div className="cv-preview">
                    <div className="cv-preview__header-bar">
                      <FileText size={16} />
                      <span>CV Önizleme</span>
                    </div>
                    <div className="cv-preview__paper">
                      {(answers.fullName || answers.email || answers.phone || answers.location) && (
                        <div className="cv-preview__personal">
                          <div className="cv-preview__avatar">
                            {candidateProfile?.imageUrl
                              ? <img src={resolveImageUrl(candidateProfile.imageUrl)} alt="" />
                              : <span>{getInitials(answers.fullName)}</span>}
                          </div>
                          <div className="cv-preview__personal-text">
                            {answers.fullName && <h2 className="cv-preview__name">{answers.fullName}</h2>}
                            {answers.title && <p className="cv-preview__headline">{answers.title}</p>}
                            <div className="cv-preview__contact">
                              {answers.email && <span><Mail size={12} /> {answers.email}</span>}
                              {answers.phone && <span><Phone size={12} /> {answers.phone}</span>}
                              {answers.location && <span><MapPin size={12} /> {answers.location}</span>}
                              {answers.linkedin && answers.linkedin.toLowerCase() !== 'yok' && (
                                <span><Link2 size={12} /> {answers.linkedin}</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {answers.summary && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><User size={14} /> Profesyonel Özet</h3>
                          <p className="cv-preview__text">{answers.summary}</p>
                        </div>
                      )}

                      {experiences.length > 0 && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Briefcase size={14} /> İş Deneyimi</h3>
                          {experiences.map((exp, i) => (
                            <div key={i} className="cv-preview__item">
                              <div className="cv-preview__item-header">
                                <strong>{capitalizeWords(exp.title || exp.company)}</strong>
                                {exp.startDate && <span className="cv-preview__date">{exp.startDate}{exp.endDate ? ` - ${exp.endDate}` : ''}</span>}
                              </div>
                              {exp.company && exp.title && <p className="cv-preview__sub">{capitalizeWords(exp.company)}</p>}
                              {exp.scope && exp.scope.toLowerCase() !== 'yok' && <p className="cv-preview__text">{exp.scope}</p>}
                              {exp.achievement && <p className="cv-preview__text"><strong>Başarılar:</strong> {exp.achievement}</p>}
                              {exp.team && exp.team.toLowerCase() !== 'yok' && <p className="cv-preview__text"><strong>Ekip/Bütçe:</strong> {exp.team}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {educations.length > 0 && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><GraduationCap size={14} /> Eğitim</h3>
                          {educations.map((edu, i) => (
                            <div key={i} className="cv-preview__item">
                              <div className="cv-preview__item-header">
                                <strong>{capitalizeWords(edu.school)}</strong>
                                {edu.startDate && <span className="cv-preview__date">{edu.startDate}{edu.endDate ? ` - ${edu.endDate}` : ''}</span>}
                              </div>
                              {edu.degree && <p className="cv-preview__sub">{capitalizeWords(edu.degree)}</p>}
                            </div>
                          ))}
                        </div>
                      )}

                      {answers.skills && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Wrench size={14} /> Yetkinlikler</h3>
                          <div className="cv-preview__skills">
                            {answers.skills.split(/[,،;]+/).map(s => s.trim()).filter(Boolean).map((skill, i) => (
                              <span key={i} className="cv-preview__skill-tag">{skill}</span>
                            ))}
                          </div>
                        </div>
                      )}

                      {((answers.certificates && answers.certificates.toLowerCase() !== 'yok') || (answers.driverLicense && answers.driverLicense.toLowerCase() !== 'yok')) && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><CheckCircle size={14} /> Sertifikalar ve Lisanslar</h3>
                          {answers.certificates && answers.certificates.toLowerCase() !== 'yok' && (
                            <p className="cv-preview__text">{capitalizeWords(answers.certificates)}</p>
                          )}
                          {answers.driverLicense && answers.driverLicense.toLowerCase() !== 'yok' && (
                            <p className="cv-preview__text">Ehliyet: {answers.driverLicense.toUpperCase()}</p>
                          )}
                        </div>
                      )}

                      {(answers.englishLevel || (answers.otherLanguages && answers.otherLanguages.toLowerCase() !== 'yok')) && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Globe size={14} /> Dil Bilgisi</h3>
                          {answers.englishLevel && answers.englishLevel !== 'none' && (
                            <p className="cv-preview__text">
                              {'İngilizce: '}{{ basic: 'Temel (A1-A2)', intermediate: 'Orta (B1-B2)', advanced: 'İleri (C1-C2)', fluent: 'Akıcı' }[answers.englishLevel]}
                            </p>
                          )}
                          {answers.otherLanguages && answers.otherLanguages.toLowerCase() !== 'yok' && (
                            <p className="cv-preview__text">{'Diğer: '}{capitalizeWords(answers.otherLanguages)}</p>
                          )}
                        </div>
                      )}

                      {extraAnswerEntries(answers).length > 0 && (
                        <div className="cv-preview__section">
                          <h3 className="cv-preview__section-title"><Info size={14} /> Ek Bilgiler</h3>
                          <div className="cv-preview__extra-grid">
                            {extraAnswerEntries(answers).map(([k, v]) => (
                              <div key={k} className="cv-preview__extra-row">
                                <span className="cv-preview__extra-label">{extraFieldLabel(k)}</span>
                                <span>{v}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {(cvSaveable && !cvSaved) && (
                      <button className="cv-builder__generate" onClick={handleChatSave} disabled={generating}>
                        {generating ? <><Loader2 size={18} className="cv-builder__spinner" /> Kaydediliyor...</> : <><CheckCircle size={18} /> CV Kaydet</>}
                      </button>
                    )}
                    {cvSaved && (
                      <p className="cv-preview__saved-badge"><CheckCircle size={14} /> CV kaydedildi</p>
                    )}
                  </div>
                )}

                {/* Chat, ALTTA */}
                <div className="cv-chat" style={{ marginTop: hasCvContent ? 20 : 0 }}>
                  <div className="cv-chat__messages">
                    {chatMessages.map((msg, i) => {
                      if (msg.role === 'section') {
                        return (
                          <div key={i} className="cv-chat__section-divider">
                            <div className="cv-chat__section-line" />
                            <span className="cv-chat__section-badge">{msg.text}</span>
                            <div className="cv-chat__section-line" />
                          </div>
                        );
                      }
                      return (
                        <div key={i} className={`cv-chat__msg cv-chat__msg--${msg.role}`}>
                          <div className="cv-chat__msg-avatar">
                            {msg.role === 'ai' ? <Sparkles size={14} /> : <User size={14} />}
                          </div>
                          <div className="cv-chat__msg-bubble">
                            {msg.text.split('\n').map((line, li) => (
                              <React.Fragment key={li}>{line}{li < msg.text.split('\n').length - 1 && <br />}</React.Fragment>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                    {chatTyping && (
                      <div className="cv-chat__msg cv-chat__msg--ai">
                        <div className="cv-chat__msg-avatar"><Sparkles size={14} /></div>
                        <div className="cv-chat__msg-bubble">
                          <div className="cv-chat__typing"><span></span><span></span><span></span></div>
                        </div>
                      </div>
                    )}
                    <div ref={chatEndRef} />
                  </div>

                  {/* Choice buttons */}
                  {isChoiceQuestion && currentQuestion.choices && (
                    <div className="cv-chat__choices">
                      {currentQuestion.choices.map((choice) => (
                        <button
                          key={choice.value}
                          className="cv-chat__choice-btn"
                          onClick={() => handleChoiceClick(choice.value)}
                        >
                          {choice.label}
                        </button>
                      ))}
                    </div>
                  )}

                  <div className="cv-chat__input-area">
                    <div className="cv-chat__input-wrap">
                      <input
                        ref={chatFileRef}
                        type="file"
                        accept=".pdf,.doc,.docx"
                        className="cv-builder__file-input"
                        id="chat-cv-upload"
                        onChange={(e) => { if (e.target.files[0]) { setUploadFile(e.target.files[0]); setUploadParsed(null); setUploadError(''); } }}
                      />
                      <label htmlFor="chat-cv-upload" className="cv-chat__upload-btn" data-tooltip="Mevcut CV dosyanızı yükleyin (PDF, DOC, DOCX)">
                        <Upload size={16} />
                      </label>

                      <textarea
                        ref={chatInputRef}
                        className="cv-chat__input"
                        rows={1}
                        placeholder={chatFinished ? 'CV\'niz hazır!' : isListening ? 'Dinliyorum...' : isChoiceQuestion ? 'Seçenek seçin veya yazın...' : 'Yazın veya mikrofona basın...'}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleChatKeyDown}
                        disabled={chatTyping || chatFinished}
                      />
                      {speechSupported && (
                        <button
                          className={`cv-chat__mic ${isListening ? 'cv-chat__mic--active' : ''}`}
                          onClick={toggleListening}
                          disabled={chatTyping || chatFinished}
                          data-tooltip={isListening ? 'Dinlemeyi durdur' : 'Sesle söyle'}
                        >
                          {isListening ? <MicOff size={16} /> : <Mic size={16} />}
                        </button>
                      )}
                      <button className="cv-chat__send" onClick={handleChatSend} disabled={!chatInput.trim() || chatTyping || chatFinished}>
                        <Send size={16} />
                      </button>
                    </div>

                    {/* Upload file badge */}
                    {uploadFile && !uploadParsed && (
                      <div className="cv-chat__upload-status">
                        <div className="cv-builder__file-badge">
                          <FileText size={14} />
                          <span>{uploadFile.name}</span>
                          <button onClick={() => { setUploadFile(null); setUploadError(''); if (chatFileRef.current) chatFileRef.current.value = ''; }}>
                            <X size={12} />
                          </button>
                        </div>
                        <button className="cv-chat__analyze-btn" onClick={handleUploadParse} disabled={uploading}>
                          {uploading ? <><Loader2 size={14} className="cv-builder__spinner" /> Analiz ediliyor...</> : <><Sparkles size={14} /> CV Analiz Et</>}
                        </button>
                      </div>
                    )}

                    {uploadParsed && (
                      <div className="cv-chat__upload-status">
                        <div className="cv-chat__upload-success">
                          <CheckCircle size={14} /> CV analiz edildi, yukarıdan kaydedebilirsiniz
                        </div>
                      </div>
                    )}

                    {uploadError && <p className="cv-builder__error" style={{ marginTop: 8 }}>{uploadError}</p>}

                    {/* Reset button */}
                    <button className="cv-chat__reset" onClick={resetChat}>
                      Baştan Başla
                    </button>
                  </div>
                </div>
          </>
        )}

        {/* ── TAB: Manual ── */}
        {activeTab === 'manual' && (
          <div className="cv-builder__form">
            <div className="cv-builder__section">
              <div className="cv-builder__field">
                <label className="cv-builder__label">CV Adı</label>
                <input className="cv-builder__input" placeholder="Örn: Garson CV, Genel CV" value={cvName} onChange={(e) => setCvName(e.target.value)} />
              </div>
            </div>

            <div className="cv-builder__section">
              <h2 className="cv-builder__section-title">Kişisel Bilgiler</h2>
              <div className="cv-builder__row">
                <input className="cv-builder__input" placeholder="Ad Soyad" value={form.fullName} onChange={(e) => updateField('fullName', e.target.value)} />
                <input className="cv-builder__input" placeholder="E-posta" type="email" value={form.email} onChange={(e) => updateField('email', e.target.value)} />
              </div>
              <div className="cv-builder__row">
                <input className="cv-builder__input" placeholder="Telefon" value={form.phone} onChange={(e) => updateField('phone', e.target.value)} />
                <input className="cv-builder__input" placeholder="Konum" value={form.location} onChange={(e) => updateField('location', e.target.value)} />
              </div>
              <textarea className="cv-builder__textarea" placeholder="Profesyonel özet..." rows={3} value={form.summary} onChange={(e) => updateField('summary', e.target.value)} />
            </div>

            <div className="cv-builder__section">
              <div className="cv-builder__section-header">
                <h2 className="cv-builder__section-title">İş Deneyimi</h2>
                <button className="cv-builder__add-btn" onClick={() => addListItem('experiences', emptyExperience)}>
                  <Plus size={14} /> Ekle
                </button>
              </div>
              {form.experiences.map((exp, i) => (
                <div key={i} className="cv-builder__list-item">
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Şirket" value={exp.company} onChange={(e) => updateListItem('experiences', i, 'company', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Pozisyon" value={exp.title} onChange={(e) => updateListItem('experiences', i, 'title', e.target.value)} />
                  </div>
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Başlangıç (2020)" value={exp.startDate} onChange={(e) => updateListItem('experiences', i, 'startDate', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Bitiş (2024 veya Devam)" value={exp.endDate} onChange={(e) => updateListItem('experiences', i, 'endDate', e.target.value)} />
                  </div>
                  <textarea className="cv-builder__textarea" placeholder="Açıklama..." rows={2} value={exp.description} onChange={(e) => updateListItem('experiences', i, 'description', e.target.value)} />
                  {form.experiences.length > 1 && (
                    <button className="cv-builder__remove-btn" onClick={() => removeListItem('experiences', i)}>
                      <Trash2 size={14} /> Kaldır
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="cv-builder__section">
              <div className="cv-builder__section-header">
                <h2 className="cv-builder__section-title">Eğitim</h2>
                <button className="cv-builder__add-btn" onClick={() => addListItem('educations', emptyEducation)}>
                  <Plus size={14} /> Ekle
                </button>
              </div>
              {form.educations.map((edu, i) => (
                <div key={i} className="cv-builder__list-item">
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Okul" value={edu.school} onChange={(e) => updateListItem('educations', i, 'school', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Bölüm / Derece" value={edu.degree} onChange={(e) => updateListItem('educations', i, 'degree', e.target.value)} />
                  </div>
                  <div className="cv-builder__row">
                    <input className="cv-builder__input" placeholder="Başlangıç" value={edu.startDate} onChange={(e) => updateListItem('educations', i, 'startDate', e.target.value)} />
                    <input className="cv-builder__input" placeholder="Bitiş" value={edu.endDate} onChange={(e) => updateListItem('educations', i, 'endDate', e.target.value)} />
                  </div>
                  {form.educations.length > 1 && (
                    <button className="cv-builder__remove-btn" onClick={() => removeListItem('educations', i)}>
                      <Trash2 size={14} /> Kaldır
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="cv-builder__section">
              <h2 className="cv-builder__section-title">Yetkinlikler</h2>
              <input className="cv-builder__input" placeholder="React, Node.js, Proje Yönetimi... (virgülle ayır)" value={form.skills} onChange={(e) => updateField('skills', e.target.value)} />
            </div>

            <button className="cv-builder__generate" onClick={handleGenerate} disabled={generating}>
              {generating ? <><Loader2 size={18} className="cv-builder__spinner" /> Oluşturuluyor...</> : <><Download size={18} /> CV Oluştur</>}
            </button>
          </div>
        )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {viewCv && ReactDOM.createPortal(
        <div className="cv-view-overlay" onClick={(e) => { if (e.target === e.currentTarget) closeViewCv(); }}>
          <div className="cv-view-modal">
            <div className="cv-view-modal__header">
              <h3>{viewCv.name || 'CV Detayı'}</h3>
              <div className="cv-view-modal__header-actions">
                {viewCv.data && (
                  <button
                    className="cv-view-modal__download"
                    title="PDF olarak indir"
                    disabled={viewCvDownloading}
                    onClick={async () => {
                      setViewCvDownloading(true);
                      try {
                        await downloadCvAsPdf(viewCv.data, viewCv.name);
                      } finally {
                        setViewCvDownloading(false);
                      }
                    }}
                  >
                    {viewCvDownloading ? <Loader2 size={18} className="cv-builder__spinner" /> : <Download size={18} />}
                  </button>
                )}
                <button className="cv-view-modal__close" onClick={closeViewCv}><X size={20} /></button>
              </div>
            </div>
            <div className="cv-view-modal__body">
              {viewCv.loading && (
                <div className="cv-view-modal__state">
                  <Loader2 size={24} className="cv-builder__spinner" />
                  <p>Yükleniyor...</p>
                </div>
              )}
              {!viewCv.loading && viewCv.error && (
                <div className="cv-view-modal__state">
                  <AlertCircle size={24} />
                  <p>{viewCv.error}</p>
                </div>
              )}
              {!viewCv.loading && !viewCv.error && viewCv.data && (
                <CvDetailPreview data={viewCv.data} />
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CvBuilderPage;
